
import { storage } from "../../firebaseClient";
import type { AttachmentMeta } from "../types/attachments";

async function uploadFilesToPath(
  basePath: string,
  docId: string,
  files: File[]
): Promise<AttachmentMeta[]> {
  const metas: AttachmentMeta[] = [];

  for (const file of files) {
    const ts = Date.now();
    // Simple sanitization
    const safeName = file.name.replace(/\s+/g, "_");
    const fullPath = `${basePath}/${docId}/${ts}-${safeName}`;
    const fileRef = storage.ref(fullPath);
    
    // v8 compat uses put()
    const snap = await fileRef.put(file);
    const url = await snap.ref.getDownloadURL();

    metas.push({
      name: file.name,
      url,
      path: fullPath,
      contentType: file.type,
      size: file.size,
      uploadedAt: new Date().toISOString(),
    });
  }

  return metas;
}

export function uploadContractFiles(
  contractId: string,
  files: File[]
): Promise<AttachmentMeta[]> {
  return uploadFilesToPath("legal-contracts", contractId, files);
}

// ... existing functions ...

export function uploadContractReviewFiles(
  reviewId: string,
  files: File[]
): Promise<AttachmentMeta[]> {
  return uploadFilesToPath("contract_reviews", reviewId, files);
}

export function uploadCaseFiles(
  matterId: string,
  files: File[]
): Promise<AttachmentMeta[]> {
  return uploadFilesToPath("legal-matters", matterId, files);
}

export const uploadLegalMatterFiles = uploadCaseFiles;

export function uploadDisputeFiles(
  disputeId: string,
  files: File[]
): Promise<AttachmentMeta[]> {
  return uploadFilesToPath("legal-disputes", disputeId, files);
}

export function uploadIpProtectionFiles(
  ipId: string,
  files: File[]
): Promise<AttachmentMeta[]> {
  return uploadFilesToPath("ip-protection", ipId, files);
}

export const uploadIpMatterFiles = uploadIpProtectionFiles;

export function uploadBrandAssetFiles(
  assetId: string,
  files: File[]
): Promise<AttachmentMeta[]> {
  return uploadFilesToPath("brand-assets", assetId, files);
}

export function uploadGovProjectFiles(
  projectId: string,
  files: File[]
): Promise<AttachmentMeta[]> {
  return uploadFilesToPath("gov-projects", projectId, files);
}

export function uploadSealFiles(
  sealId: string,
  files: File[]
): Promise<AttachmentMeta[]> {
  return uploadFilesToPath("seal-applications", sealId, files);
}

export function uploadGovCorrespondenceFiles(
  govId: string,
  files: File[]
): Promise<AttachmentMeta[]> {
  return uploadFilesToPath("gov-correspondence", govId, files);
}

export function uploadTaskFiles(
  taskId: string,
  files: File[]
): Promise<AttachmentMeta[]> {
  return uploadFilesToPath("legal-tasks", taskId, files);
}

export function uploadTemplateFiles(
  templateId: string,
  files: File[]
): Promise<AttachmentMeta[]> {
  return uploadFilesToPath("legal-templates", templateId, files);
}

export function uploadLawyerFiles(
  lawyerId: string,
  files: File[]
): Promise<AttachmentMeta[]> {
  return uploadFilesToPath("legal-lawyers", lawyerId, files);
}

export function uploadGovernanceDocFiles(
  docId: string,
  files: File[]
): Promise<AttachmentMeta[]> {
  return uploadFilesToPath("governance_core_docs", docId, files);
}

export function uploadWorkLogFiles(
  logId: string,
  files: File[]
): Promise<AttachmentMeta[]> {
  return uploadFilesToPath("legal-work-logs", logId, files);
}

/**
 * Upload a file to a temporary location for processing (e.g. AI Analysis)
 * The file is not associated with a permanent record yet.
 */
export async function uploadTempFile(file: File): Promise<AttachmentMeta> {
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    const results = await uploadFilesToPath("temp/uploads", tempId, [file]);
    return results[0];
}
